import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import Button from '../shared/Button';
import { useTheme } from '../../context/ThemeContext';
import { usePermissions } from '../../hooks/usePermissions';
import { subscribeToNotifications } from '../../services/business/notificationService';
import { PERMISSIONS, MENU_PERMISSIONS } from '../../constants/permissions';

const Sidebar = ({ userData }) => {
    const { theme } = useTheme();
    const { can, loading: permLoading } = usePermissions(userData);
    const [collapsed, setCollapsed] = useState(false);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        if (!userData?.tenantId || !userData?.uid) return;

        const unsubscribe = subscribeToNotifications(userData.tenantId, userData.uid, (data) => {
            const unread = data.filter(n => !n.read).length;
            setUnreadCount(unread);
        });

        return () => unsubscribe();
    }, [userData?.tenantId, userData?.uid]);


    const menuItems = [
        { label: 'Dashboard', path: '/', icon: '📊' },
        { label: 'Bildirimler', path: '/notifications', icon: '🔔', badge: unreadCount },
        { label: 'Profilim', path: '/profile', icon: '👤' },

        {
            category: 'Operasyon', items: [
                { label: 'Tedarikçi Yönetimi', path: '/coordinator/transporters', icon: '🏢' },
                { label: 'Araç Planlama', path: '/coordinator/planning', icon: '🚚' },
                { label: 'Rezervasyon Yönetimi', path: '/coordinator/reservation-management', icon: '📅' },
                { label: 'Rampa Durumu', path: '/coordinator/docks', icon: '🚧' },
                { label: 'Rezervasyon Takip', path: '/operations/tracking', icon: '🛰️' },
                { label: 'Nakliyeci Performansı', path: '/analysis/carrier-performance', icon: '📈' },
            ]
        },

        {
            category: 'Nakliye', items: [
                { label: 'Araçlar', path: '/transporter/vehicles', icon: '🚛' },
                { label: 'Sürücüler', path: '/transporter/drivers', icon: '👨‍✈️' },
                { label: 'Rezervasyonlar', path: '/transporter/reservations', icon: '🎫' },
            ]
        },

        {
            category: 'Tesis', items: [
                { label: 'Hızlı Giriş/Çıkış', path: '/gate/process', icon: '⚡' },
                { label: 'Yükleme Listesi', path: '/dock/loading', icon: '📋' },
            ]
        },

        {
            category: 'Yönetim', items: [
                { label: 'Kullanıcı Yönetimi', path: '/admin/users', icon: '👥' },
                { label: 'Yetki Yönetimi', path: '/admin/permissions', icon: '🔐' },
                { label: 'Sistem Ayarları', path: '/admin/settings', icon: '⚙️' },
            ]
        }
    ];

    // Filter items based on permissions
    const getFilteredMenu = () => {
        return menuItems.map(item => {
            if (item.items) {
                const subItems = item.items.filter(sub => {
                    const resource = MENU_PERMISSIONS[sub.path];
                    if (!resource) return true;
                    return can(resource, PERMISSIONS.VIEW);
                });
                return subItems.length > 0 ? { ...item, items: subItems } : null;
            }

            const resource = MENU_PERMISSIONS[item.path];
            if (!resource) return item;
            return can(resource, PERMISSIONS.VIEW) ? item : null;
        }).filter(Boolean);
    };

    const filteredItems = getFilteredMenu();

    // Define elite active styles per theme
    const activeStyles = {
        dark: {
            background: 'linear-gradient(90deg, var(--primary-color), var(--secondary-color))',
            color: 'white',
            shadow: '0 0 15px var(--card-glow), 0 4px 10px rgba(0, 0, 0, 0.3)'
        },
        light: {
            background: 'linear-gradient(135deg, #2563eb, #1e40af)',
            color: '#ffffff',
            shadow: '0 0 15px var(--card-glow), 0 6px 20px rgba(37, 99, 235, 0.25)'
        },
        sepia: {
            background: 'linear-gradient(135deg, #92400e, #78350f)',
            color: '#fdf6e3',
            shadow: '0 0 15px var(--card-glow), 0 4px 12px rgba(146, 64, 14, 0.3)'
        },
        midnight: {
            background: 'linear-gradient(135deg, #6366f1, #ec4899)',
            color: 'white',
            shadow: '0 0 20px var(--card-glow), 0 4px 15px rgba(99, 102, 241, 0.4)'
        },
        pure: {
            background: 'white',
            color: 'black',
            shadow: '0 0 20px rgba(255, 255, 255, 0.8)'
        }
    };

    const currentActive = activeStyles[theme] || activeStyles.dark;

    const renderLink = (item) => (
        <NavLink
            key={item.path}
            to={item.path}
            title={collapsed ? item.label : ''}
            onMouseEnter={() => setHoveredItem(item.path)}
            onMouseLeave={() => setHoveredItem(null)}
            className={({ isActive }) => `flex items-center ${collapsed ? 'justify-center' : ''} ${isActive ? 'active-menu-item' : ''}`}
            style={({ isActive }) => {
                const isHovered = hoveredItem === item.path;

                // Base Styles
                let bg = 'transparent';
                let col = 'var(--text-secondary)';
                let extraStyles = {};

                // Active State (Priority)
                if (isActive) {
                    bg = currentActive.background;
                    col = currentActive.color;
                }
                // Hover State
                else if (isHovered) {
                    const darkThemes = ['dark', 'midnight'];

                    if (!darkThemes.includes(theme)) {
                        // "Cream 3D" Effect for Light/Sepia/Pure/Default
                        bg = '#fffcf5';
                        col = '#1a1a1a';
                        // Add shadow/transform logic in return object
                        extraStyles = {
                            boxShadow: '0 8px 20px -5px rgba(0,0,0,0.1), inset 0 0 0 1px rgba(255,255,255,1)',
                            transform: 'translateY(-2px) scale(1.02)',
                            border: '1px solid rgba(0,0,0,0.05)',
                            zIndex: 10
                        };
                    } else {
                        // Dark themes hover
                        bg = theme === 'midnight' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.1)';
                        col = 'white';
                    }
                }

                return {
                    padding: '10px 14px',
                    borderRadius: '12px',
                    background: bg,
                    color: col,
                    fontWeight: isActive ? '700' : '500',
                    textDecoration: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: isActive ? '1px solid rgba(255,255,255,0.1)' : '1px solid transparent',
                    fontSize: '0.85rem',
                    boxShadow: isActive ? currentActive.shadow : (isHovered && extraStyles.boxShadow ? extraStyles.boxShadow : 'none'),
                    transform: isActive ? 'translateX(2px)' : (isHovered && extraStyles.transform ? extraStyles.transform : (isHovered ? 'translateX(2px)' : 'none')),
                    gap: collapsed ? '0' : 'var(--spacing-md)',
                    marginBottom: '4px',
                    whiteSpace: 'nowrap',
                    ...extraStyles // Apply other extra styles like border and zIndex
                };
            }}
        >
            <div style={{ position: 'relative' }}>
                <span style={{ fontSize: '1.2rem', minWidth: '24px', textAlign: 'center', display: 'block' }}>
                    {item.icon}
                </span>
                {collapsed && item.badge > 0 && (
                    <span
                        className="absolute -top-2 -right-2 flex items-center justify-center shadow-sm animate-pulse"
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            borderRadius: '50%',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            width: '18px',
                            height: '18px',
                            border: '2px solid white'
                        }}
                    >
                        {item.badge > 9 ? '9+' : item.badge}
                    </span>
                )}
            </div>
            {!collapsed && (
                <div className="flex-1 flex justify-between items-center ml-3">
                    <span className="truncate">{item.label}</span>
                    {item.badge > 0 && (
                        <span
                            className="flex items-center justify-center shadow-sm ml-auto"
                            style={{
                                background: '#ef4444',
                                color: 'white',
                                borderRadius: '50%',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                minWidth: '22px',
                                height: '22px',
                                padding: '0 4px'
                            }}
                        >
                            {item.badge > 99 ? '99+' : item.badge}
                        </span>
                    )}
                </div>
            )}
        </NavLink>
    );

    return (
        <aside
            className="card-premium glow-theme"
            style={{
                height: 'calc(100vh - 120px)',
                width: collapsed ? '75px' : '260px',
                position: 'sticky',
                top: '100px',
                transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 90,
                padding: 'var(--spacing-md) var(--spacing-sm)',
                overflow: 'hidden',
                marginRight: 'var(--spacing-sm)'
            }}
        >
            <div className="flex flex-col gap-xs" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                {!collapsed && (
                    <div style={{
                        padding: '0 var(--spacing-md) var(--spacing-sm)',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-bold)',
                        color: 'var(--text-secondary)',
                        opacity: 0.8,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        Ana Menü
                        <div
                            title="Yardım & Destek"
                            style={{ cursor: 'pointer', opacity: 0.7, fontSize: '1rem' }}
                            onClick={() => window.open('https://github.com/mertakan/antigravity/docs', '_blank')}
                        >
                            ℹ️
                        </div>
                    </div>
                )}
                {collapsed && <div className="text-center mb-md" style={{ fontSize: '1.2rem' }}>📑</div>}

                {filteredItems.map((item, idx) => {
                    if (item.category && !collapsed) {
                        return (
                            <div key={`cat-${idx}`} style={{ marginTop: idx === 0 ? 0 : '16px' }}>
                                <div style={{
                                    padding: '0 14px 8px',
                                    fontSize: '0.65rem',
                                    color: 'var(--text-secondary)',
                                    opacity: 0.8,
                                    fontWeight: 800,
                                    textTransform: 'uppercase'
                                }}>
                                    {item.category}
                                </div>
                                {item.items.map(subItem => renderLink(subItem))}
                            </div>
                        );
                    }
                    if (item.items) {
                        return item.items.map(subItem => renderLink(subItem));
                    }
                    return renderLink(item);
                })}
            </div>

            {/* Toggle Button at Bottom */}
            <div style={{
                paddingTop: 'var(--spacing-md)',
                marginTop: 'auto',
                borderTop: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: collapsed ? 'center' : 'flex-end',
                paddingRight: collapsed ? '0' : 'var(--spacing-md)'
            }}>
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--text-secondary)',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.2s'
                    }}
                    title={collapsed ? "Menüyü Genişlet" : "Menüyü Daralt"}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                    {collapsed ? '»' : '«'} Menüyü Sakla
                </button>
            </div>
        </aside>
    );
};

export default Sidebar;
